import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toSearchParams } from '../../js/lib/sharedSearchService';
import { addAlert, canViewWaivers, removeAlert } from '../../js/configTdm';
import _ from 'lodash';
import moment from 'moment';
import DateSelector from '../../js/lib/DateSelector/DateSelector';
import WaiverSelector from '../../js/waivers/WaiverSelector';
import { SuggestAirline, SuggestAirport } from '../../js/reference/AutoSuggests';
import { SuggestAirlineProps, SuggestAirportProps } from '../../js/headersegment/interfaces';
import '../../styles/modules/waiver-reporting.less';
import { DATE_PICKER_DEFAULT_FORMAT } from '../../js/constants';
import { validateField } from '../../js/lib/filters';

const TripsTemplate: React.FC<any> = ({ getWaiverData, advFormData, searchApply }) => {
  const [advForm, setAdvForm] = useState<any>(advFormData);
  const [fieldTouched, setFieldTouched] = useState({} as any);
  const [errorFields, setErrorFields] = useState([] as String[]);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const navigate = useNavigate();
  const invalidCriteriaMessage = 'Search criteria is invalid';
  const canSearchWaivers = () => canViewWaivers();
  const [hasNonNullValues, setHasNonNullValues] = useState(false);
  useEffect(() => setHasNonNullValues(Object.values(advForm).some(
    (value) => value !== null && value !== undefined && value !== ""
  )), [advForm]);
  const dateOfTravelObj = {
    dateOptions: {
      formatYear: 'yy',
      startingDay: 0,
      showWeeks: false,
    },
    format: DATE_PICKER_DEFAULT_FORMAT
  };

  useEffect(() => {
    return () => {
      setAdvForm({
        recordLocator: null,
        tripGroup: null,
        firstName: null,
        lastName: null,
        email: null,
        airline: null,
        flightNumber: null,
        dateOfTravelBegin: null,
        dateOfTravelEnd: null,
        depAirport: null,
        arrAirport: null,
        waiver: null,
      });
    }
  }, []);

  useEffect(() => {
    if (errorFields && Object.keys(errorFields).length) {
      setSubmitDisabled(true);
    } else {
      setSubmitDisabled(false);
    }
  }, [errorFields]);

  const submitAdv = useCallback((isInvalid: boolean) => {
    if (isInvalid) {
      addAlert(invalidCriteriaMessage);
      return;
    }

    if (advForm.dateOfTravelBegin && advForm.dateOfTravelEnd) {
      if (moment(advForm.dateOfTravelEnd).isBefore(moment(advForm.dateOfTravelBegin))) {
        addAlert(invalidCriteriaMessage);
        return;
      }
    }

    const params = toSearchParams(advForm);
    if (params.length) {
      refreshSearchTrips(params.join('&'));
    }
  }, [advForm]);

  const refreshSearchTrips = (params: string) => {
    const currentPath = location.pathname + location.search;
    const targetPath = `/trips/search?${params}`;

    if (currentPath === targetPath) {
      searchApply();
    } else {
      navigate(targetPath);
      searchApply();
    }
  };

  const reset = () => {
    setAdvForm({
      recordLocator: null,
      tripGroup: null,
      firstName: null,
      lastName: null,
      email: null,
      airline: null,
      flightNumber: null,
      dateOfTravelBegin: null,
      dateOfTravelEnd: null,
      depAirport: null,
      arrAirport: null,
      waiver: null,
    });
    setFieldTouched({});
    setErrorFields([]);
    setSubmitDisabled(true);
    if (removeAlert) {
      removeAlert(invalidCriteriaMessage);
    }
    navigate(location.pathname);
    searchApply();
  };

  const formStateUpdate = (keyName: string, value: any, type: string, e?: React.ChangeEvent<HTMLInputElement>) => {
    setFieldTouched({ ...fieldTouched, [keyName]: true });
    const hasError = validateField(type, value, e);
    if (hasError) {
      setErrorFields([...errorFields, keyName]);
    } else {
      const newErrorFields = errorFields.filter((field) => field !== keyName);
      setErrorFields(newErrorFields);
    }
    setAdvForm({ ...advForm, [keyName]: value });
  };

  return (
    <form
      name="searchform"
      role="form"
      noValidate
    >
      <div className="row trips-search">
        <div className="col-sm-6 col-md-2">
          <label htmlFor="record-locator" className="db-text">Record Locator</label>
          <input
            id="record-locator"
            type="text"
            className={`form-control italic-placeholder ${errorFields.includes('recordLocator') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['recordLocator'] ? 'ng-dirty' : ''}`}
            value={advForm.recordLocator || ''}
            onChange={e =>
              formStateUpdate('recordLocator', e.target.value, 'text', e)
            }
            minLength={5}
            maxLength={50}
            placeholder="ex. A1B2C3"
          />
        </div>
        <div className="col-sm-6 col-md-2">
          <label htmlFor="trip-group" className="db-text">Trip Group Name</label>
          <input
            id="trip-group"
            type="text"
            className={`form-control italic-placeholder ${errorFields.includes('tripGroup') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['tripGroup'] ? 'ng-dirty' : ''}`}
            value={advForm.tripGroup || ''}
            onChange={e =>
              formStateUpdate('tripGroup', e.target.value, 'text', e)
            }
          />
        </div>
        <div className="col-sm-6 col-md-2">
          <label htmlFor="first-name" className="db-text">First Name</label>
          <input
            id="first-name"
            type="text"
            className={`form-control italic-placeholder ${errorFields.includes('firstName') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['firstName'] ? 'ng-dirty' : ''}`}
            value={advForm.firstName || ''}
            onChange={e =>
              formStateUpdate('firstName', e.target.value, 'text', e)
            }
          />
        </div>
        <div className="col-sm-6 col-md-3">
          <label htmlFor="last-name" className="db-text">Last Name</label>
          <input
            id="last-name"
            type="text"
            className={`form-control italic-placeholder ${errorFields.includes('lastName') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['lastName'] ? 'ng-dirty' : ''}`}
            value={advForm.lastName || ''}
            onChange={e =>
              formStateUpdate('lastName', e.target.value, 'text', e)
            }
          />
        </div>
        <div className="col-sm-12 col-md-3">
          <label htmlFor="email" className="db-text">Email Address</label>
          <input
            id="email"
            type="email"
            className={`form-control italic-placeholder ${errorFields.includes('email') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['email'] ? 'ng-dirty' : ''}`}
            value={advForm.email || ''}
            onChange={e =>
              formStateUpdate('email', e.target.value, 'email', e)
            }
            placeholder="john@doe.com"
          />
        </div>
        <div className="col-sm-8 col-md-3">
          <label htmlFor="airline" className="db-text">Airline</label>
          <SuggestAirline value={advForm.airline || ''} className={`${errorFields.includes('airline') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['airline'] ? 'ng-dirty' : ''}`} callback={(value: SuggestAirlineProps) => {
            formStateUpdate('airline', value, 'select');
          }}></SuggestAirline>
        </div>
        <div className="col-sm-4 col-md-1">
          <label htmlFor="flight" className="db-text">Flight #</label>
          <input
            id="flight"
            type="number"
            className={`form-control ${errorFields.includes('flightNumber') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['flightNumber'] ? 'ng-dirty' : ''}`}
            value={advForm.flightNumber || ''}
            onChange={e =>
              formStateUpdate('flightNumber', e.target.value, 'number', e)
            }
            onKeyDown={(e) => {
              if (!/^[0-9]$/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') {
                e.preventDefault();
              }
            }}
            maxLength={4}
          />
        </div>
        <div className="col-sm-6 col-md-4 col-lg-2">
          <DateSelector
            key={`date-of-travel-begin`}
            label="Departure On or After"
            value={advForm.dateOfTravelBegin}
            dateFormat={dateOfTravelObj.format}
            onChange={value =>
              formStateUpdate('dateOfTravelBegin', value, 'date')
            }
          />
        </div>
        <div className="col-sm-6 col-md-4 col-lg-2 trip-search-date-input">
          <DateSelector
            key={`date-of-travel-end`}
            label="Departure On or Before"
            value={advForm.dateOfTravelEnd}
            dateFormat={dateOfTravelObj.format}
            onChange={value =>
              formStateUpdate('dateOfTravelEnd', value, 'date')
            }
          />
        </div>
        <div className="col-sm-6 col-md-6 col-lg-2">
          <label htmlFor="departure-airport" className="db-text">Departure Airport</label>
          <SuggestAirport id='departure-airport' value={advForm.depAirport || ''} className={`${errorFields.includes('depAirport') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['depAirport'] ? 'ng-dirty' : ''}`} callback={(value: SuggestAirportProps) => {
            formStateUpdate('depAirport', value, 'select')
          }}></SuggestAirport>
        </div>
        <div className="col-sm-6 col-md-6 col-lg-2">
          <label htmlFor="arrival-airport" className="db-text">Arrival Airport</label>
          <SuggestAirport id='arrival-airport' value={advForm.arrAirport || ''} className={`${errorFields.includes('arrAirport') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['arrAirport'] ? 'ng-dirty' : ''}`} callback={(value: SuggestAirportProps) => {
            formStateUpdate('arrAirport', value, 'select')
          }}></SuggestAirport>
        </div>
      </div>
      <div className="row">
        <div className="col-md-4">
          {canSearchWaivers() && <div>
            <label htmlFor="waiver" className="db-text">Search for Trips with Waiver Name</label>
            <WaiverSelector className="waiver-selector"
              onChange={waiver => {
                formStateUpdate('waiver', waiver, 'select');
                getWaiverData(waiver);
              }}
              initialWaiver={advForm.waiver} id="waiver">
            </WaiverSelector>
          </div>}
        </div>
        <div className="col-md-4"></div>
        <div className="col-md-4 search-button">
          <div className="form-group form-group-sep">
            <button
              className="btn btn-default button-clear"
              type="button"
              onClick={reset}
              disabled={!hasNonNullValues}
            >CLEAR</button>
            <button
              className="btn btn-default button-action"
              type="button"
              onClick={() => submitAdv(submitDisabled)}
              disabled={!hasNonNullValues}
            >SEARCH</button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default TripsTemplate;